/**
 * Enhanced ApplicationList Component with virtualization and memoization
 * 
 * Displays a list of podcast guest applications with filtering, search, and virtualized rendering
 * for improved performance with large datasets.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApplications } from './useApplications';
import { ErrorBoundary } from '@/shared/components/feedback/ErrorBoundary';
import { Application, ApprovalStatus } from '@/types/models/schema';
import { ApiResponse } from '@/types/api';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  RefreshCw, 
  Filter,
  ArrowUpDown
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';

// Define sort value types
type SortableValue = string | number | null | undefined;

/**
 * Sort data based on field and direction
 * 
 * @param a First item to compare
 * @param b Second item to compare
 * @param field Field to sort by
 * @param direction Sort direction ('asc' or 'desc')
 * @returns Sort comparison result
 */
const sortData = <T extends Record<string, unknown>>(
  a: T,
  b: T,
  field: keyof T,
  direction: 'asc' | 'desc'
): number => {
  const aValue = a[field] as SortableValue;
  const bValue = b[field] as SortableValue;
  
  if (typeof aValue === 'string' && typeof bValue === 'string') {
    return direction === 'asc' 
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  }
  
  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return direction === 'asc' 
      ? aValue - bValue
      : bValue - aValue;
  }
  
  // Handle null/undefined values
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return direction === 'asc' ? -1 : 1;
  if (bValue == null) return direction === 'asc' ? 1 : -1;
  
  return 0;
};

// Application status badge component (memoized)
const StatusBadge: React.FC<{ status: ApprovalStatus | 'converted' }> = React.memo(({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'converted':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}>
      {status}
    </span>
  );
});

StatusBadge.displayName = 'StatusBadge';

// Application row component (memoized)
interface ApplicationRowProps {
  application: Application;
  onConvert: (id: string) => void;
  style?: React.CSSProperties;
}

const ApplicationRow = React.memo(({ application, onConvert, style }: ApplicationRowProps) => {
  const handleConvert = useCallback(() => {
    if (application.id) {
      onConvert(application.id);
    }
  }, [application.id, onConvert]);

  return (
    <div className="border-b border-gray-200 hover:bg-gray-50" style={style}>
      <div className="grid grid-cols-12 px-6 py-4">
        <div className="col-span-3">
          <div className="text-sm font-medium text-gray-900">{application.applicant_name}</div>
          <div className="text-sm text-gray-500">{application.applicant_email}</div>
        </div>
        <div className="col-span-3">
          <div className="text-sm text-gray-900">{application.company || ''}</div>
          <div className="text-sm text-gray-500">{application.title || ''}</div>
        </div>
        <div className="col-span-2">
          <StatusBadge status={application.status} />
        </div>
        <div className="col-span-2">
          <div className="text-sm text-gray-900">
            {application.created_at 
              ? new Date(application.created_at).toLocaleDateString() 
              : 'Unknown date'}
          </div>
        </div>
        <div className="col-span-2 text-right text-sm font-medium">
          <Link
            to={`${ROUTES.APPLICATIONS}/${application.id}`}
            className="text-primary-600 hover:text-primary-900 mr-4"
          >
            View
          </Link>
          {application.status === 'approved' && application.id && (
            <button
              onClick={handleConvert}
              className="text-green-600 hover:text-green-900"
            >
              Convert
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

ApplicationRow.displayName = 'ApplicationRow';

interface ApplicationFilters {
  search?: string;
  status?: string;
}

const ApplicationList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof Application>('applicant_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  const { 
    applications, 
    isLoading, 
    error,
    filters, 
    setFilters, 
    fetchApplications, 
    convertToGuest,
    retryFetch
  } = useApplications();

  // Handle search input change (memoized)
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Handle search submission (memoized)
  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchTerm });
  }, [filters, searchTerm, setFilters]);

  // Handle status filter change (memoized)
  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const status = e.target.value || undefined;
    setFilters({ ...filters, status });
  }, [filters, setFilters]);

  // Handle convert to guest (memoized)
  const handleConvertToGuest = useCallback(async (id: string) => {
    if (window.confirm('Are you sure you want to convert this application to a guest?')) {
      await convertToGuest(id);
    }
  }, [convertToGuest]);

  // Handle clear filters (memoized)
  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setFilters({});
    fetchApplications({});
  }, [fetchApplications, setFilters]);

  // Handle retry (memoized)
  const handleRetry = useCallback(() => {
    retryFetch();
  }, [retryFetch]);

  // Handle sort toggle (memoized)
  const handleSort = useCallback((field: keyof Application) => {
    setSortField(prevField => {
      if (prevField === field) {
        setSortDirection(prevDirection => prevDirection === 'asc' ? 'desc' : 'asc');
        return prevField;
      } else {
        setSortDirection('asc');
        return field;
      }
    });
  }, []);

  // Safe access to applications data with type checking and memoization
  const applicationData = useMemo(() => {
    return applications?.data || [];
  }, [applications]);

  // Filter and sort applications with proper null/undefined handling
  const filteredApplications = useMemo(() => {
    return applicationData.filter(application => {
      const nameMatch = application.applicant_name?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
      const emailMatch = application.applicant_email?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
      const companyMatch = application.company?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
      const titleMatch = application.title?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
      
      return nameMatch || emailMatch || companyMatch || titleMatch;
    });
  }, [applicationData, searchTerm]);

  // Sort filtered applications
  const sortedApplications = useMemo(() => {
    return [...filteredApplications].sort((a, b) => 
      sortData(a, b, sortField, sortDirection)
    );
  }, [filteredApplications, sortField, sortDirection]);

  // Row renderer for virtualized list (memoized)
  const rowRenderer = useCallback(({ index, style }: { index: number, style: React.CSSProperties }) => {
    const application = sortedApplications[index];
    if (!application) return null;
    
    return (
      <ApplicationRow
        application={application}
        onConvert={handleConvertToGuest}
        style={style}
      />
    );
  }, [sortedApplications, handleConvertToGuest]);

  // Loading state component (memoized)
  const renderLoading = useMemo(() => (
    <div className="p-8 text-center">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500 mb-2"></div>
      <p className="text-gray-500">Loading applications...</p>
    </div>
  ), []);

  // Error state component (memoized)
  const renderError = useMemo(() => (
    <div className="p-8 text-center">
      <p className="text-red-500 mb-2">Failed to load applications</p>
      <Button onClick={handleRetry} variant="outline" className="flex items-center gap-2">
        <RefreshCw size={16} />
        <span>Retry</span>
      </Button>
    </div>
  ), [handleRetry]);

  // Empty state component (memoized)
  const renderEmpty = useMemo(() => (
    <div className="p-8 text-center">
      <p className="text-gray-500">No applications found. Try adjusting your filters.</p>
      <Button
        onClick={handleClearFilters}
        variant="outline"
        className="mt-4 flex items-center gap-2"
      >
        <Filter size={16} />
        <span>Clear Filters</span>
      </Button>
    </div>
  ), [handleClearFilters]);

  // Table header component (memoized)
  const renderTableHeader = useMemo(() => (
    <div className="grid grid-cols-12 px-6 py-3 bg-gray-50 border-b border-gray-200">
      <div 
        className="col-span-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer flex items-center gap-1"
        onClick={() => handleSort('applicant_name')}
      >
        <span>Applicant</span>
        <ArrowUpDown size={14} />
      </div>
      <div 
        className="col-span-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer flex items-center gap-1"
        onClick={() => handleSort('company')}
      >
        <span>Company</span>
        <ArrowUpDown size={14} />
      </div>
      <div 
        className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer flex items-center gap-1"
        onClick={() => handleSort('status')}
      >
        <span>Status</span>
        <ArrowUpDown size={14} />
      </div>
      <div 
        className="col-span-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer flex items-center gap-1"
        onClick={() => handleSort('created_at')}
      >
        <span>Date</span>
        <ArrowUpDown size={14} />
      </div>
      <div className="col-span-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
        Actions
      </div>
    </div>
  ), [handleSort]);

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 font-heading">Applications</h1>
            <p className="text-neutral-500 mt-1">
              Manage podcast guest applications
            </p>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Application List</CardTitle>
            <CardDescription>
              {sortedApplications.length} applications found
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {/* Filters */}
            <form onSubmit={handleSearch} className="flex flex-wrap gap-4 mb-6">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={18} />
                <Input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search by name, email, or company"
                  className="pl-10"
                  aria-label="Search applications"
                />
              </div>
              <div className="w-40">
                <select
                  id="status"
                  value={filters.status || ''}
                  onChange={handleStatusChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Filter by status"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="converted">Converted</option>
                </select>
              </div>
              <div className="flex items-end gap-2">
                <Button type="submit">
                  Search
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleClearFilters}
                >
                  Clear
                </Button>
              </div>
            </form>

            {/* Application List */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {isLoading ? (
                renderLoading
              ) : error ? (
                renderError
              ) : sortedApplications.length > 0 ? (
                <>
                  {renderTableHeader}
                  <div style={{ height: Math.min(500, sortedApplications.length * 60) }}>
                    <AutoSizer>
                      {({ height, width }: { height: number, width: number }) => (
                        <List
                          height={height}
                          width={width}
                          itemCount={sortedApplications.length}
                          itemSize={60}
                          overscanCount={5}
                        >
                          {rowRenderer}
                        </List>
                      )}
                    </AutoSizer>
                  </div>
                </>
              ) : (
                renderEmpty
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </ErrorBoundary>
  );
};

export default React.memo(ApplicationList);
